import type { IndeedIntegrationSettings } from 'api-clients/monolith';
import { useId } from 'react';
import { FormattedMessage } from 'react-intl';

import { Label } from '@/components/ui/label';
import { RadioGroup, RadioGroupItem } from '@/components/ui/radio-group';
import { Separator } from '@/components/ui/separator';
import { Switch } from '@/components/ui/switch';
import { Typography } from '@/components/ui/typography';
import type { HeldIndeedToggles } from '@/utils/indeed-connect-storage';

export type IndeedSettingsValues = Required<HeldIndeedToggles['changed']>;

export function getIndeedSettingsVisibility(
  settings: IndeedIntegrationSettings,
) {
  const showTitleSource = !(
    settings.indeed_brand_based && settings.brand_based_user
  );
  return {
    showTitleSource,
    showToggles:
      showTitleSource && settings.indeed_partnership_settings_enabled,
  };
}

export function IndeedSettingsFields({
  settings,
  value,
  onChange,
  organicPreset = false,
}: {
  settings: IndeedIntegrationSettings;
  value: IndeedSettingsValues;
  onChange: (value: IndeedSettingsValues) => void;
  organicPreset?: boolean;
}) {
  const id = useId();
  const { showTitleSource, showToggles } =
    getIndeedSettingsVisibility(settings);
  if (!showTitleSource) return null;

  return (
    <>
      {showToggles && (
        <>
          <Separator />
          <div className="flex flex-col gap-2">
            <div className="flex items-center gap-2">
              <Switch
                id={`${id}-organic`}
                checked={value.indeed_xml_feed_enabled}
                onCheckedChange={checked =>
                  onChange({ ...value, indeed_xml_feed_enabled: checked })
                }
                aria-describedby={`${id}-organic-description`}
              />
              <Label htmlFor={`${id}-organic`} className="!font-medium">
                <FormattedMessage
                  defaultMessage="Enable Indeed Organic Feed"
                  description="Allow active openings to appear in Indeed's free job feed"
                />
              </Label>
            </div>
            <Typography variant="subtitle1" id={`${id}-organic-description`}>
              {organicPreset && (
                <>
                  <FormattedMessage
                    defaultMessage="Turns on when you save"
                    description="The free Indeed job feed will be enabled only after saving"
                  />
                  <br />
                </>
              )}
              <FormattedMessage
                defaultMessage="Post your active openings to Indeed for free. If enabled, all active openings will be shared with Indeed."
                description="Explains which openings are shared through Indeed's organic feed"
              />
            </Typography>
            <Typography variant="subtitle1">
              <FormattedMessage
                defaultMessage="Indeed maintains stringent <link>Job Posting Standards</link> that can negatively affect the performance of your organic sponsored jobs. Review your opening norms before activating to ensure optimized performance."
                description="Indeed's posting standards can affect organic job performance"
                values={{
                  link: chunks => (
                    <a
                      className="text-fx-brand-600 underline"
                      href="https://support.fountain.com/en/articles/9899632-indeed-guidelines"
                      target="_blank"
                      rel="noopener noreferrer"
                    >
                      {chunks}
                    </a>
                  ),
                }}
              />
            </Typography>
          </div>
          <Separator />
          <div className="flex flex-col gap-2">
            <div className="flex items-center gap-2">
              <Switch
                id={`${id}-apply`}
                checked={value.indeed_apply_enabled}
                onCheckedChange={checked =>
                  onChange({ ...value, indeed_apply_enabled: checked })
                }
                aria-describedby={`${id}-apply-description`}
              />
              <Label htmlFor={`${id}-apply`} className="!font-medium">
                <FormattedMessage
                  defaultMessage="Enable Indeed Apply"
                  description="Allow applicants to start their application on Indeed"
                />
              </Label>
            </div>
            <Typography variant="subtitle1" id={`${id}-apply-description`}>
              <FormattedMessage
                defaultMessage="Allow your applicants to complete their initial application within Indeed before continuing their hiring process in Fountain. <link>Learn more about Indeed Apply</link>."
                description="Explains how Indeed Apply starts an application before continuing in Fountain"
                values={{
                  link: chunks => (
                    <a
                      className="text-fx-brand-600 underline"
                      href="https://indeed.my.site.com/employerSupport1/s/article/What-is-Indeed-Apply"
                      target="_blank"
                      rel="noopener noreferrer"
                    >
                      {chunks}
                    </a>
                  ),
                }}
              />
            </Typography>
          </div>
        </>
      )}
      <Separator />
      <div className="flex flex-col gap-2">
        <Typography variant="body2" id={`${id}-title`}>
          <FormattedMessage
            defaultMessage="Sourcing Feed Title Source"
            description="The source of the job title displayed on Indeed"
          />
        </Typography>
        <Typography variant="subtitle1" id={`${id}-title-description`}>
          <FormattedMessage
            defaultMessage="Select which title source you want Indeed to use to display jobs on Indeed."
            description="Choose whether Indeed displays opening names or position names"
          />
        </Typography>
        <RadioGroup
          className="flex flex-col gap-2"
          value={value.sourcing_title_source}
          aria-labelledby={`${id}-title`}
          aria-describedby={`${id}-title-description`}
          onValueChange={source => {
            if (source === 'funnel_title' || source === 'position_title')
              onChange({ ...value, sourcing_title_source: source });
          }}
        >
          <div className="flex items-center gap-2">
            <RadioGroupItem id={`${id}-opening`} value="funnel_title" />
            <Label htmlFor={`${id}-opening`}>
              <FormattedMessage
                defaultMessage="Opening name"
                description="Use the opening's name as the Indeed job title"
              />
            </Label>
          </div>
          <div className="flex items-center gap-2">
            <RadioGroupItem id={`${id}-position`} value="position_title" />
            <Label htmlFor={`${id}-position`}>
              <FormattedMessage
                defaultMessage="Position name"
                description="Use the position's name as the Indeed job title"
              />
            </Label>
          </div>
        </RadioGroup>
      </div>
    </>
  );
}
